import React from "react";
import editIcon from "assets/icons/edit-icon.svg";
import binIcon from "assets/icons/bin-icon.svg";
import eyeIcon from "assets/icons/eye.svg";
import eyeOffIcon from "assets/icons/eye-off.svg";
import inactiveIcon from "assets/icons/inactive.svg";
import { t } from "i18next";
import ConfirmTooltip from "components/confirmTooltip/ConfirmTooltip";
import styles from "./Options.module.scss";

type Props = {
  id: number | string;
  onEdit?: (v: number | string) => void;
  onDelete?: (v: number | string) => void;
  hasEdit?: boolean;
  hasDelete?: boolean;
  instantDelete?: boolean;
  activeConfirmId?: number | string | null;
  setActiveConfirmId?: (id: number | string | null) => void;
  hasView?: boolean;
  onView?: (v: number | string) => void;
  isViewActive?: boolean;
  hasInactive?: boolean;
  onInactive?: () => void;
};

export const Options: React.FC<Props> = ({
  id,
  onEdit,
  onDelete,
  hasDelete,
  hasEdit,
  instantDelete,
  activeConfirmId,
  setActiveConfirmId,
  hasView,
  onView,
  isViewActive,
  hasInactive,
  onInactive,
}) => {
  const handleClickEditModal = (id: number | string) => {
    if (onEdit) {
      onEdit(id);
    }
  };
  const handleClickView = (id: number | string) => {
    if (onView) {
      onView(id);
    }
  };
  const handleConfirmDelete = (id: number | string) => {
    if (onDelete) {
      onDelete(id);
    }
    setActiveConfirmId?.(null);
  };
  const handleToggleConfirm = (next: boolean) => {
    setActiveConfirmId?.(next ? id : null);
  };
  const handleKeyboardAction = (
    event: React.KeyboardEvent<HTMLDivElement>,
    action: () => void,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  return (
    <div
      className={`options ${styles["options"]} ${
        [hasDelete, hasEdit, hasView, hasInactive].filter(Boolean).length === 1
          ? styles["single"]
          : ""
      }`}
    >
      {hasView && (
        <div
          className={styles["option"]}
          role="button"
          tabIndex={0}
          aria-label={isViewActive ? "Close view" : "View"}
          onClick={() => handleClickView(id)}
          onKeyDown={(event) =>
            handleKeyboardAction(event, () => handleClickView(id))
          }
        >
          <img
            src={isViewActive ? eyeOffIcon : eyeIcon}
            alt={isViewActive ? "Close View Icon" : "View Icon"}
          />
        </div>
      )}
      {hasEdit && (
        <div
          className={styles["option"]}
          role="button"
          tabIndex={0}
          aria-label="Edit"
          onClick={() => handleClickEditModal(id)}
          onKeyDown={(event) =>
            handleKeyboardAction(event, () => handleClickEditModal(id))
          }
        >
          <img src={editIcon} alt="Edit Icon" />
        </div>
      )}
      {hasInactive && (
        <div
          className={`${styles["option"]} ${styles["inactive"]}`}
          role="button"
          tabIndex={0}
          aria-label="Action unavailable"
          onClick={onInactive}
          onKeyDown={(event) =>
            handleKeyboardAction(event, () => onInactive?.())
          }
        >
          <img src={inactiveIcon} alt="Action unavailable" />
        </div>
      )}

      {hasDelete &&
        (instantDelete ? (
          <div
            className={styles.option}
            role="button"
            tabIndex={0}
            aria-label="Delete"
            onClick={() => handleConfirmDelete(id)}
            onKeyDown={(event) =>
              handleKeyboardAction(event, () => handleConfirmDelete(id))
            }
          >
            <img src={binIcon} alt="" />
          </div>
        ) : (
          <>
            <div
              data-tooltip-id={`delete-tooltip-${id}`}
              data-tooltip-place="left"
              className={styles["option"]}
              role="button"
              tabIndex={0}
              aria-label="Delete"
              onKeyDown={(event) =>
                handleKeyboardAction(event, () => handleToggleConfirm(true))
              }
            >
              <img src={binIcon} alt="" />
            </div>
            <ConfirmTooltip
              id={`delete-tooltip-${id}`}
              title={t("areYouSureYouWantDeleteThisEntity")}
              onConfirm={() => handleConfirmDelete(id)}
              isOpen={activeConfirmId === id}
              onToggle={handleToggleConfirm}
              config={{
                clickable: true,
                openEvents: { click: true },
                closeEvents: { click: true, blur: true },
                position: "right",
              }}
            />
          </>
        ))}
    </div>
  );
};
